import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { formatMoney, BuyerMonthlyAnalytics } from '@dill/dill-shared';
import { Calendar } from '@phosphor-icons/react';
import { useAppDispatch } from '../../../../redux/store';
import { fetchSupplierBuyerMonthlyAnalytics } from '../../../../redux/services/supplierBuyersService';
import InvoiceVolumeChart from '../../mainBuyers/MainBuyerDashboard/InvoiceVolumeChart';
import DSOTrendChart from '../../mainBuyers/MainBuyerDashboard/DSOTrendChart';
import CreditUtilizationChart from '../../mainBuyers/MainBuyerDashboard/CreditUtilizationChart';
import AppDateRangePickerModal from '../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import type {
  InvoiceVolumePoint,
  DSOTrendPoint,
  CreditUtilizationPoint,
} from '../../mainBuyers/MainBuyerDashboard/chartTypes';

interface SupplierCustomerDashboardProps {
  supplierId: string;
  buyerId: string;
}

const toChartMonth = (ym: string) => {
  const [y, m] = ym.split('-');
  return `${y}-${new Date(parseInt(y), parseInt(m) - 1, 1)
    .toLocaleString('en-US', { month: 'short' })
    .toUpperCase()}`;
};

const ChartSkeleton = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="h-2.5 w-24 rounded bg-gray-200" />
      <div className="h-4 w-16 rounded bg-gray-200" />
    </div>
    <div className="h-[120px] rounded-lg bg-gray-100" />
  </div>
);

export const SupplierCustomerDashboard = ({
  supplierId,
  buyerId,
}: SupplierCustomerDashboardProps) => {
  const dispatch = useAppDispatch();
  const [monthlyAnalytics, setMonthlyAnalytics] = useState<BuyerMonthlyAnalytics[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedMonths, setFetchedMonths] = useState(12);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(),
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  useEffect(() => {
    if (!supplierId || !buyerId) return;
    setLoading(true);
    dispatch(
      fetchSupplierBuyerMonthlyAnalytics({ supplierId, buyerId, months: fetchedMonths })
    ).then((result: any) => {
      if (result?.payload?.data?.monthlyAnalytics) {
        setMonthlyAnalytics(result.payload.data.monthlyAnalytics);
      }
      setLoading(false);
    });
  }, [supplierId, buyerId, dispatch, fetchedMonths]);

  // If the selected start date goes further back than what's been fetched, re-fetch
  useEffect(() => {
    const now = new Date();
    const required =
      (now.getFullYear() - dateRange.startDate.getFullYear()) * 12 +
      (now.getMonth() - dateRange.startDate.getMonth()) +
      1;
    if (required > fetchedMonths) {
      setFetchedMonths(required);
    }
  }, [dateRange.startDate, fetchedMonths]);

  const paddedAnalytics = useMemo(() => {
    const now = new Date();
    const existing = new Map(monthlyAnalytics.map((a) => [a.month, a]));
    const months: string[] = [];
    let cur = new Date(dateRange.startDate.getFullYear(), dateRange.startDate.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    while (cur <= end) {
      months.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    const empty = (month: string) => {
      const [y, m] = month.split('-');
      const yr = parseInt(y);
      const mo = parseInt(m);
      return {
        id: month,
        buyerId: '',
        supplierId: '',
        mainBuyerId: null as string | null | undefined,
        month,
        year: y,
        periodStart: new Date(yr, mo - 1, 1),
        periodEnd: new Date(yr, mo, 0),
        lastComputedAt: new Date(0),
        creditLimit: 0,
        openAR: 0,
        utilizationPercent: 0,
        totalInvoiced: 0,
        invoiceCount: 0,
        totalPaid: 0,
        dso: 0,
        dsoPaymentCount: 0,
        createdAt: new Date(0),
        updatedAt: new Date(0),
      };
    };
    return months.map((key) => existing.get(key) ?? empty(key));
  }, [monthlyAnalytics, dateRange.startDate]);

  const sortedAnalytics = useMemo(
    () => [...paddedAnalytics].sort((a, b) => a.month.localeCompare(b.month)),
    [paddedAnalytics]
  );

  const filteredAnalytics = useMemo(() => {
    const startMonth = moment(dateRange.startDate).format('YYYY-MM');
    const endMonth = moment(dateRange.endDate).format('YYYY-MM');
    return sortedAnalytics.filter((a) => a.month >= startMonth && a.month <= endMonth);
  }, [sortedAnalytics, dateRange]);

  const invoiceVolumeData: InvoiceVolumePoint[] = useMemo(
    () =>
      filteredAnalytics.map((a) => ({
        month: toChartMonth(a.month),
        totalInvoiced: a.totalInvoiced,
      })),
    [filteredAnalytics]
  );

  const dsoTrendData: DSOTrendPoint[] = useMemo(
    () => filteredAnalytics.map((a) => ({ month: toChartMonth(a.month), dso: a.dso })),
    [filteredAnalytics]
  );

  const creditUtilizationData: CreditUtilizationPoint[] = useMemo(
    () =>
      filteredAnalytics.map((a) => ({
        month: toChartMonth(a.month),
        openAR: a.openAR,
        creditLimit: a.creditLimit,
        utilizationPercent: a.utilizationPercent,
      })),
    [filteredAnalytics]
  );

  const lastEntry = filteredAnalytics[filteredAnalytics.length - 1];
  // Invoice volume is a flow, so the headline is the total over the selected range (YTD by
  // default); DSO and utilization are point-in-time, so they show the latest month.
  const totalInvoiceVolume = filteredAnalytics.reduce((sum, a) => sum + (a.totalInvoiced || 0), 0);
  const latestDSO = lastEntry?.dso ?? 0;
  const latestUtilizationPct = lastEntry?.utilizationPercent ?? 0;

  const formatDateRangeDisplay = () => {
    const start = moment(dateRange.startDate).format('MMM DD, YYYY');
    const end = moment(dateRange.endDate).format('MMM DD, YYYY');
    return `${start} – ${end}`;
  };

  if (loading) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pt-4 pb-4">
          <ChartSkeleton />
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="flex justify-end pt-4 pb-2">
        <button
          onClick={() => setIsDatePickerOpen(true)}
          className="inline-flex items-center gap-2 h-[34px] px-3.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 cursor-pointer transition-colors whitespace-nowrap">
          <Calendar size={16} weight="bold" />
          <span>{formatDateRangeDisplay()}</span>
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pb-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
              Invoice Volume
            </h3>
            <span className="text-sm font-medium text-gray-900 tabular-nums">
              {formatMoney(totalInvoiceVolume, 0)}
            </span>
          </div>
          <InvoiceVolumeChart data={invoiceVolumeData} />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
              DSO Trend
            </h3>
            <span className="text-sm font-medium text-gray-900 tabular-nums">{latestDSO} days</span>
          </div>
          <DSOTrendChart data={dsoTrendData} />
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
              Credit Utilization
            </h3>
            <span className="text-sm font-medium text-gray-900 tabular-nums">
              {latestUtilizationPct.toFixed(1)}%
            </span>
          </div>
          <CreditUtilizationChart data={creditUtilizationData} />
        </div>
      </div>
      <AppDateRangePickerModal
        open={isDatePickerOpen}
        handleClose={() => setIsDatePickerOpen(false)}
        onSelectDateRange={(range) => {
          setDateRange({
            startDate: range.startDate ?? dateRange.startDate,
            endDate: range.endDate ?? dateRange.endDate,
          });
          setIsDatePickerOpen(false);
        }}
        selectedDateRange={dateRange}
      />
    </div>
  );
};
